import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Eye, EyeOff, AlertCircle } from 'lucide-react';
import DoctorService from './Doctor/DoctorService';
import Cookies from 'js-cookie';
import './login.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError('Please enter both username and password');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const response = await DoctorService.login(username, password);
      if (response && response.success) {
        const cookieOpts = { expires: 1 };
        Cookies.set('adminid', String(response.data.adminid), cookieOpts);
        Cookies.set('adminname', String(response.data.adminname), cookieOpts);
        Cookies.set('role', String(response.data.role), cookieOpts);
        Cookies.set('token', String(response.data.token), cookieOpts);
        localStorage.setItem('adminid', String(response.data.adminid));
        localStorage.setItem('adminname', String(response.data.adminname));
        localStorage.setItem('role', String(response.data.role));
        localStorage.setItem('token', String(response.data.token));
        window.location.href = '/dashboard';
      } else {
        setError((response && response.data && response.data.message) || (response && response.message) || 'Invalid credentials');
      }
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 font-sans">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col lg:flex-row" style={{ minHeight: '600px' }}>

        {/* Left Brand Panel */}
        <div
          className="lg:w-[45%] flex-shrink-0 relative flex flex-col justify-between p-10 overflow-hidden"
          style={{ background: 'linear-gradient(145deg, #0f172a 0%, #0f2d2b 55%, #134e4a 100%)' }}
        >
          <div aria-hidden="true" className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle, #99f6e4 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
          <div aria-hidden="true" className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #0d9488, transparent 70%)' }} />
          <div aria-hidden="true" className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #0f766e, transparent 70%)' }} />

          <div className="relative z-10">
            <h1 className="text-white font-extrabold text-2xl tracking-tight">Super Admin</h1>
            <p className="text-teal-400 text-[10px] font-bold tracking-[0.2em] uppercase mt-1">Management Portal</p>
          </div>

          <div className="relative z-10 py-10">
            <h2 className="text-[1.75rem] font-extrabold text-white leading-tight mb-4">
              Healthcare Operations,<br />
              <span className="text-teal-300">Under One Roof.</span>
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed max-w-[260px]">
              A unified command centre for hospitals, clinics, doctors, and pharmacies — built for administrators who need clarity at scale.
            </p>
          </div>

          <div className="relative z-10 flex flex-col gap-2.5">
            {[
              { label: 'Facilities managed', value: '500+' },
              { label: 'Platform uptime', value: '99.9%' },
              { label: 'Compliance', value: 'HIPAA · DISHA' },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <span className="text-slate-400 text-[11px]">{label}</span>
                <span className="text-teal-300 text-[11px] font-bold tracking-wide">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="flex-1 flex flex-col justify-center px-8 py-12 md:px-14 bg-white">
          <div className="mb-9">
            <p className="text-[10px] font-bold tracking-[0.16em] uppercase text-teal-600 mb-2">Super Admin Portal</p>
            <h1 className="text-[1.75rem] font-extrabold text-slate-900 leading-tight">Sign in to your account</h1>
            <p className="text-slate-500 text-sm mt-1.5">Enter your credentials to access the dashboard.</p>
          </div>

          {error && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-100 text-red-700 px-4 py-3.5 rounded-xl mb-7 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[0.8rem] font-semibold text-slate-600 mb-1.5">Username</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <User className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="block w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-900 text-sm placeholder-slate-400 outline-none transition-all focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                />
              </div>
            </div>

            <div>
              <label className="block text-[0.8rem] font-semibold text-slate-600 mb-1.5">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="block w-full pl-10 pr-11 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-900 text-sm placeholder-slate-400 outline-none transition-all focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-teal-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full mt-1.5 py-3.5 px-6 rounded-xl text-white text-sm font-bold tracking-wide transition-all flex items-center justify-center gap-2 ${
                loading ? 'bg-slate-300 cursor-not-allowed' : 'bg-gradient-to-r from-teal-600 to-teal-500 shadow-lg shadow-teal-200/60 hover:brightness-110 active:scale-[0.99]'
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Signing in…
                </>
              ) : 'Sign In'}
            </button>
          </form>

          <p className="mt-10 text-center text-[11px] text-slate-400 tracking-wide">
            Orange Health · Operations Management Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
